import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	Label,
	ResponsiveTable,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Skeleton,
	Textarea,
} from "@ancatag/at-design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Key, MoreVertical, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { type ApiKeysFilters, apiKeysService } from "../../services/admin";
import type { ApiKey } from "../../types/admin";

export default function AdminApiKeys() {
	const [page, setPage] = useState(1);
	const [limit] = useState(50);
	const [filters, setFilters] = useState<ApiKeysFilters>({});
	const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
	const [detailOpen, setDetailOpen] = useState(false);
	const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
	const [blockDialogOpen, setBlockDialogOpen] = useState(false);
	const [blockReason, setBlockReason] = useState("");

	const queryClient = useQueryClient();

	const { data: apiKeysData, isLoading } = useQuery({
		queryKey: ["admin-api-keys", page, limit, filters],
		queryFn: () => apiKeysService.getApiKeys(page, limit, filters),
	});

	// Fetch full API key details when selected
	const { data: apiKeyDetails } = useQuery({
		queryKey: ["admin-api-key-details", selectedApiKey?.id],
		queryFn: () => {
			if (!selectedApiKey?.id) throw new Error("API key ID is required");
			return apiKeysService.getApiKey(selectedApiKey.id);
		},
		enabled: !!selectedApiKey && detailOpen,
	});

	// Mutations
	const revokeMutation = useMutation({
		mutationFn: (apiKeyId: string) => apiKeysService.revokeApiKey(apiKeyId),
		onSuccess: () => {
			toast.success("API key revoked successfully");
			queryClient.invalidateQueries({ queryKey: ["admin-api-keys"] });
			setRevokeDialogOpen(false);
		},
		onError: (error: any) => {
			toast.error(error.message || "Failed to revoke API key");
		},
	});

	const blockMutation = useMutation({
		mutationFn: ({ apiKeyId, reason }: { apiKeyId: string; reason: string }) =>
			apiKeysService.blockApiKey(apiKeyId, reason),
		onSuccess: () => {
			toast.success("API key blocked successfully");
			queryClient.invalidateQueries({ queryKey: ["admin-api-keys"] });
			setBlockDialogOpen(false);
			setBlockReason("");
		},
		onError: (error: any) => {
			toast.error(error.message || "Failed to block API key");
		},
	});

	const apiKeys = apiKeysData?.data || [];
	const meta = apiKeysData?.meta;

	const handleApiKeyClick = (apiKey: ApiKey) => {
		setSelectedApiKey(apiKey);
		setDetailOpen(true);
	};

	const handleRevoke = () => {
		if (selectedApiKey) {
			revokeMutation.mutate(selectedApiKey.id);
		}
	};

	const handleBlock = () => {
		if (selectedApiKey && blockReason.trim()) {
			blockMutation.mutate({
				apiKeyId: selectedApiKey.id,
				reason: blockReason,
			});
		}
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						API Key Management
					</h1>
					<p className="text-muted-foreground mt-2">
						View and manage all API keys across the platform
					</p>
				</div>
			</div>

			{/* Filters */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Filters</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Status</Label>
							<Select
								value={filters.status || "all"}
								onValueChange={(value) => {
									setFilters({
										...filters,
										status: value === "all" ? undefined : (value as any),
									});
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All Statuses" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Statuses</SelectItem>
									<SelectItem value="active">Active</SelectItem>
									<SelectItem value="inactive">Inactive</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Blocked</Label>
							<Select
								value={
									filters.isBlocked === undefined
										? "all"
										: filters.isBlocked
											? "blocked"
											: "not-blocked"
								}
								onValueChange={(value) => {
									setFilters({
										...filters,
										isBlocked:
											value === "all" ? undefined : value === "blocked",
									});
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All</SelectItem>
									<SelectItem value="blocked">Blocked</SelectItem>
									<SelectItem value="not-blocked">Not Blocked</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Search</Label>
							<Input
								placeholder="Search by name or key..."
								value={filters.search || ""}
								onChange={(e) => {
									setFilters({ ...filters, search: e.target.value });
									setPage(1);
								}}
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* API Keys Table */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						API Keys {meta && `(${meta.total.toLocaleString()} total)`}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<ApiKeysTableSkeleton />
					) : (
						<>
							<ResponsiveTable
								data={apiKeys}
								keyExtractor={(key) => key.id}
								columns={[
									{
										key: "user",
										header: "User",
										mobileLabel: "User",
										render: (key) => (
											<div>
												<p className="text-sm font-medium">
													{key.project.user.email}
												</p>
												<p className="text-xs text-muted-foreground">
													{key.project.user.name || "—"}
												</p>
											</div>
										),
									},
									{
										key: "project",
										header: "Project",
										mobileLabel: "Project",
										render: (key) => (
											<span className="text-sm">{key.project.name}</span>
										),
									},
									{
										key: "name",
										header: "Name",
										mobileLabel: "Name",
										render: (key) => (
											<span className="text-sm font-medium">{key.name}</span>
										),
									},
									{
										key: "key",
										header: "Key",
										mobileLabel: "Key",
										render: (key) => (
											<span className="text-sm font-mono">
												{key.keyPrefix}...
											</span>
										),
									},
									{
										key: "status",
										header: "Status",
										mobileLabel: "Status",
										render: (key) => (
											<Badge
												variant={key.isActive ? "default" : "secondary"}
												className={
													key.isActive
														? "bg-green-500/20 text-green-600 border-green-500/30"
														: "bg-red-500/20 text-red-600 border-red-500/30"
												}
											>
												{key.isActive ? "Active" : "Inactive"}
											</Badge>
										),
									},
									{
										key: "requests",
										header: "Requests",
										mobileLabel: "Requests",
										hideOnMobile: true,
										render: (key) => (
											<span className="text-sm">
												{key.totalRequests.toLocaleString()}
											</span>
										),
									},
									{
										key: "lastUsed",
										header: "Last Used",
										mobileLabel: "Last Used",
										hideOnMobile: true,
										render: (key) => (
											<span className="text-sm">
												{key.lastUsedAt
													? new Date(key.lastUsedAt).toLocaleDateString()
													: "Never"}
											</span>
										),
									},
									{
										key: "created",
										header: "Created",
										mobileLabel: "Created",
										hideOnMobile: true,
										render: (key) => (
											<span className="text-sm">
												{new Date(key.createdAt).toLocaleDateString()}
											</span>
										),
									},
									{
										key: "actions",
										header: "Actions",
										mobileLabel: "Actions",
										render: (key) => (
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="ghost" size="icon">
														<MoreVertical className="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													<DropdownMenuItem
														onClick={() => handleApiKeyClick(key)}
													>
														<Eye className="h-4 w-4 mr-2" />
														View Details
													</DropdownMenuItem>
													{key.isActive && (
														<>
															<DropdownMenuItem
																onClick={() => {
																	setSelectedApiKey(key);
																	setRevokeDialogOpen(true);
																}}
															>
																<XCircle className="h-4 w-4 mr-2" />
																Revoke
															</DropdownMenuItem>
															<DropdownMenuItem
																onClick={() => {
																	setSelectedApiKey(key);
																	setBlockDialogOpen(true);
																}}
															>
																<Key className="h-4 w-4 mr-2" />
																Block
															</DropdownMenuItem>
														</>
													)}
												</DropdownMenuContent>
											</DropdownMenu>
										),
									},
								]}
								onRowClick={handleApiKeyClick}
								emptyMessage="No API keys found matching your filters."
							/>

							{/* Pagination */}
							{meta && meta.totalPages > 1 && (
								<div className="flex items-center justify-between mt-4">
									<div className="text-sm text-muted-foreground">
										Page {meta.page} of {meta.totalPages}
									</div>
									<div className="flex items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={!meta.hasPrev}
										>
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												setPage((p) => Math.min(meta.totalPages, p + 1))
											}
											disabled={!meta.hasNext}
										>
											Next
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>

			{/* API Key Detail Dialog */}
			<Dialog open={detailOpen} onOpenChange={setDetailOpen}>
				<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>API Key Details</DialogTitle>
						<DialogDescription>
							{selectedApiKey?.name} - Full API key information
						</DialogDescription>
					</DialogHeader>
					{apiKeyDetails && (
						<div className="space-y-4 mt-4">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<Label className="text-xs text-muted-foreground">Name</Label>
									<p className="text-sm font-medium">{apiKeyDetails.name}</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Status
									</Label>
									<Badge
										variant={apiKeyDetails.isActive ? "default" : "secondary"}
									>
										{apiKeyDetails.isActive ? "Active" : "Inactive"}
									</Badge>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Key Prefix
									</Label>
									<p className="text-sm font-mono">
										{apiKeyDetails.keyPrefix}...
									</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Environment
									</Label>
									<p className="text-sm">{apiKeyDetails.environment}</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">User</Label>
									<p className="text-sm">{apiKeyDetails.project.user.email}</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Project
									</Label>
									<p className="text-sm">{apiKeyDetails.project.name}</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Total Requests
									</Label>
									<p className="text-sm">
										{apiKeyDetails.totalRequests.toLocaleString()}
									</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Last Used
									</Label>
									<p className="text-sm">
										{apiKeyDetails.lastUsedAt
											? new Date(apiKeyDetails.lastUsedAt).toLocaleString()
											: "Never"}
									</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Created
									</Label>
									<p className="text-sm">
										{new Date(apiKeyDetails.createdAt).toLocaleString()}
									</p>
								</div>
								{apiKeyDetails.expiresAt && (
									<div>
										<Label className="text-xs text-muted-foreground">
											Expires At
										</Label>
										<p className="text-sm">
											{new Date(apiKeyDetails.expiresAt).toLocaleString()}
										</p>
									</div>
								)}
							</div>

							{/* Usage Records */}
							{apiKeyDetails.usageRecords &&
								apiKeyDetails.usageRecords.length > 0 && (
									<div>
										<Label className="text-xs text-muted-foreground mb-2 block">
											Recent Usage ({apiKeyDetails.usageRecords.length})
										</Label>
										<div className="space-y-2">
											{apiKeyDetails.usageRecords.map((record) => (
												<Card key={record.id}>
													<CardContent className="p-3">
														<div className="flex items-center justify-between">
															<div>
																<p className="text-sm font-mono">
																	{record.requestId}
																</p>
																<p className="text-xs text-muted-foreground">
																	{record.tokensTotal} tokens,{" "}
																	{record.tokensSaved} saved
																</p>
															</div>
															<Badge
																variant={
																	record.cacheHit ? "default" : "secondary"
																}
															>
																{record.cacheHit ? "Cached" : "Generated"}
															</Badge>
														</div>
													</CardContent>
												</Card>
											))}
										</div>
									</div>
								)}

							{/* Actions */}
							{apiKeyDetails.isActive && (
								<div className="flex flex-wrap gap-2 pt-4 border-t">
									<Button
										variant="outline"
										onClick={() => {
											setRevokeDialogOpen(true);
										}}
									>
										<XCircle className="h-4 w-4 mr-2" />
										Revoke Key
									</Button>
									<Button
										variant="destructive"
										onClick={() => {
											setBlockDialogOpen(true);
										}}
									>
										<Key className="h-4 w-4 mr-2" />
										Block Key
									</Button>
								</div>
							)}
						</div>
					)}
				</DialogContent>
			</Dialog>

			{/* Revoke Dialog */}
			<Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Revoke API Key</DialogTitle>
						<DialogDescription>
							Are you sure you want to revoke this API key? This action cannot
							be undone.
						</DialogDescription>
					</DialogHeader>
					<div className="flex justify-end gap-2 mt-4">
						<Button
							variant="outline"
							onClick={() => setRevokeDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={handleRevoke}
							disabled={revokeMutation.isPending}
						>
							{revokeMutation.isPending ? "Revoking..." : "Revoke Key"}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Block Dialog */}
			<Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Block API Key</DialogTitle>
						<DialogDescription>
							Block this API key with a reason. This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 mt-4">
						<div className="space-y-2">
							<Label htmlFor="block-reason">Reason *</Label>
							<Textarea
								id="block-reason"
								placeholder="Enter reason for blocking..."
								value={blockReason}
								onChange={(e) => setBlockReason(e.target.value)}
								required
							/>
						</div>
						<div className="flex justify-end gap-2">
							<Button
								variant="outline"
								onClick={() => {
									setBlockDialogOpen(false);
									setBlockReason("");
								}}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={handleBlock}
								disabled={blockMutation.isPending || !blockReason.trim()}
							>
								{blockMutation.isPending ? "Blocking..." : "Block Key"}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}

function ApiKeysTableSkeleton() {
	return (
		<div className="space-y-2">
			{[1, 2, 3, 4, 5].map((i) => (
				<Skeleton key={i} className="h-12 w-full" />
			))}
		</div>
	);
}
